import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  LayoutDashboard, Truck, History, DollarSign, ListFilter, Plus, ArrowUp,
  Users, Car, Bell, Settings, MapPin, Calendar, Clock, Package, Eye,
  Filter, Search, MoreVertical, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { useTransporterStats } from '@/hooks/useTransporterStats';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getTransporterSidebarLinks } from '@/config/transporterSidebarLinks';

const TransporterHistory = () => {
  const { stats } = useTransporterStats();
  const [activeTab, setActiveTab] = useState('missions');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    date_range: 'all',
  });

  // Données simulées pour l'historique
  const [missionsHistory] = useState([
    {
      id: '1',
      status: 'COMPLETED',
      from: 'Conakry',
      to: 'Kankan',
      date: '2024-01-15',
      price: 2500000,
      client: 'Alpha Diallo',
      vehicle: 'AB-123-CD',
      driver: 'Mamadou Bah',
    },
    {
      id: '2',
      status: 'COMPLETED',
      from: 'Conakry',
      to: 'Labé',
      date: '2024-01-12',
      price: 1800000,
      client: 'Fatou Camara',
      vehicle: 'EF-456-GH',
      driver: 'Ibrahima Sow',
    },
    {
      id: '3',
      status: 'CANCELLED',
      from: 'Conakry',
      to: 'N\'Zérékoré',
      date: '2024-01-10',
      price: 3200000,
      client: 'Oumar Barry',
      vehicle: 'IJ-789-KL',
      driver: 'Sékou Traoré',
    },
  ]);

  const [paymentsHistory] = useState([
    {
      id: '1',
      amount: 2500000,
      status: 'SUCCESS',
      method: 'ORANGE_MONEY',
      date: '2024-01-15',
      mission: 'Mission #1',
    },
    {
      id: '2',
      amount: 1800000,
      status: 'SUCCESS',
      method: 'MTN_MOMO',
      date: '2024-01-12',
      mission: 'Mission #2',
    },
    {
      id: '3',
      amount: 3200000,
      status: 'FAILED',
      method: 'BANK_TRANSFER',
      date: '2024-01-10',
      mission: 'Mission #3',
    },
  ]);

  const [offersHistory] = useState([
    {
      id: '1',
      request_id: 'REQ-001',
      price: 1500000,
      status: 'ACCEPTED',
      date: '2024-01-14',
      client: 'Aminata Keita',
      from: 'Conakry',
      to: 'Kindia',
    },
    {
      id: '2',
      request_id: 'REQ-002',
      price: 2200000,
      status: 'REJECTED',
      date: '2024-01-13',
      client: 'Mohamed Fofana',
      from: 'Conakry',
      to: 'Mamou',
    },
    {
      id: '3',
      request_id: 'REQ-003',
      price: 1900000,
      status: 'PENDING',
      date: '2024-01-11',
      client: 'Kadiatou Diallo',
      from: 'Conakry',
      to: 'Faranah',
    },
  ]);

  const sidebarLinks = getTransporterSidebarLinks(stats);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'COMPLETED': { label: 'Terminée', color: 'bg-green-500', icon: CheckCircle },
      'CANCELLED': { label: 'Annulée', color: 'bg-red-500', icon: XCircle },
      'IN_PROGRESS': { label: 'En cours', color: 'bg-blue-500', icon: Clock },
      'SUCCESS': { label: 'Réussi', color: 'bg-green-500', icon: CheckCircle },
      'FAILED': { label: 'Échoué', color: 'bg-red-500', icon: XCircle },
      'PENDING': { label: 'En attente', color: 'bg-yellow-500', icon: Clock },
      'ACCEPTED': { label: 'Acceptée', color: 'bg-green-500', icon: CheckCircle },
      'REJECTED': { label: 'Rejetée', color: 'bg-red-500', icon: XCircle },
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-500', icon: AlertTriangle };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getPaymentMethodIcon = (method) => {
    const iconConfig = {
      'ORANGE_MONEY': '🟠',
      'MTN_MOMO': '🟡',
      'BANK_TRANSFER': '🏦',
      'CARD': '💳',
      'PLATFORM_BALANCE': '💰',
    };

    return iconConfig[method] || '💳';
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    // Afficher le nombre complet avec séparateurs de milliers
    return `${Number(amount).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} GNF`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <>
      <Helmet>
        <title>Historique - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Historique</h1>
            <p className="text-muted-foreground mt-2">Consultez l'historique de vos activités</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="missions">Missions</TabsTrigger>
            <TabsTrigger value="payments">Paiements</TabsTrigger>
            <TabsTrigger value="offers">Offres</TabsTrigger>
          </TabsList>

          {/* Filtres communs */}
          <Card className="mb-6 bg-card/50 border-border">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder="Rechercher..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filtrer par statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="COMPLETED">Terminé</SelectItem>
                    <SelectItem value="CANCELLED">Annulé</SelectItem>
                    <SelectItem value="IN_PROGRESS">En cours</SelectItem>
                    <SelectItem value="SUCCESS">Réussi</SelectItem>
                    <SelectItem value="FAILED">Échoué</SelectItem>
                    <SelectItem value="PENDING">En attente</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.date_range} onValueChange={(value) => setFilters(prev => ({ ...prev, date_range: value }))}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Période" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les périodes</SelectItem>
                    <SelectItem value="today">Aujourd'hui</SelectItem>
                    <SelectItem value="week">Cette semaine</SelectItem>
                    <SelectItem value="month">Ce mois</SelectItem>
                    <SelectItem value="quarter">Ce trimestre</SelectItem>
                    <SelectItem value="year">Cette année</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Onglet Missions */}
          <TabsContent value="missions" className="space-y-4">
            {missionsHistory.length === 0 ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="p-12 text-center">
                  <Truck className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucune mission dans l'historique</h3>
                  <p className="text-muted-foreground">Vos missions terminées apparaîtront ici.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {missionsHistory.map((mission) => (
                  <Card key={mission.id} className="bg-card/50 border-border hover:border-primary/50 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Truck className="w-5 h-5" />
                            <h3 className="text-lg font-semibold">Mission #{mission.id}</h3>
                            {getStatusBadge(mission.status)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{mission.from} → {mission.to}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Date: {formatDate(mission.date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              <span>Client: {mission.client}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Car className="w-4 h-4" />
                              <span>Véhicule: {mission.vehicle}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-3">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-primary">
                              {formatCurrency(mission.transporter_amount || 0)}
                            </div>
                            <div className="text-sm text-muted-foreground">Prix</div>
                          </div>

                          <div className="flex gap-2">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" />
                              Détails
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Onglet Paiements */}
          <TabsContent value="payments" className="space-y-4">
            {paymentsHistory.length === 0 ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="p-12 text-center">
                  <DollarSign className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucun paiement dans l'historique</h3>
                  <p className="text-muted-foreground">Vos paiements apparaîtront ici.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {paymentsHistory.map((payment) => (
                  <Card key={payment.id} className="bg-card/50 border-border hover:border-primary/50 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">{getPaymentMethodIcon(payment.method)}</span>
                            <h3 className="text-lg font-semibold">Paiement #{payment.id}</h3>
                            {getStatusBadge(payment.status)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Date: {formatDate(payment.date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Truck className="w-4 h-4" />
                              <span>Mission: {payment.mission}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4" />
                              <span>Méthode: {payment.method}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-3">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-primary">
                              {formatCurrency(payment.amount)}
                            </div>
                            <div className="text-sm text-muted-foreground">Montant</div>
                          </div>

                          <div className="flex gap-2">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" />
                              Détails
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Onglet Offres */}
          <TabsContent value="offers" className="space-y-4">
            {offersHistory.length === 0 ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="p-12 text-center">
                  <ListFilter className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucune offre dans l'historique</h3>
                  <p className="text-muted-foreground">Vos offres apparaîtront ici.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {offersHistory.map((offer) => (
                  <Card key={offer.id} className="border-border bg-primary-700/20 hover:border-secondary transition-colors">
                    <CardContent className="p-6">
                      <div className="flex flex-col gap-6">
                        <div className="space-y-6">
                          {/* En-tête avec numéro d'offre, statut et prix */}
                          <div className="space-y-3 pb-4 border-b border-border">
                            <div className="flex items-center gap-3 flex-wrap">
                              <h3 className="text-lg font-bold text-foreground">Offre #{offer.id}</h3>
                              {getStatusBadge(offer.status)}
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-baseline gap-3">
                                <div className="text-3xl font-bold text-secondary">
                                  {formatCurrency(offer.price)}
                                </div>
                                <div className="text-sm text-muted-foreground">Prix proposé</div>
                              </div>
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2 pt-2">
                              <Calendar className="w-4 h-4" />
                              <div>
                                <span className="font-medium">Date: </span>
                                <span>{formatDate(offer.date)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Informations du trajet */}
                          <div className="bg-primary-700/30 rounded-lg p-3 border border-primary-700/50">
                            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              Trajet
                            </h4>
                            <div className="space-y-1.5 text-sm">
                              <div className="flex items-center gap-2 text-foreground">
                                <span className="font-medium">De:</span>
                                <span className="truncate">{offer.from || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-foreground">
                                <span className="font-medium">Vers:</span>
                                <span className="truncate">{offer.to || 'N/A'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Informations client */}
                          {offer.client && (
                            <div className="bg-primary-700/30 rounded-lg p-3 border border-primary-700/50">
                              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Client
                              </h4>
                              <div className="text-sm font-semibold text-foreground">
                                {offer.client}
                              </div>
                            </div>
                          )}

                          {/* Véhicule */}
                          {offer.vehicle && (
                            <div className="bg-primary-700/30 rounded-lg p-3 border border-primary-700/50">
                              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                                <Truck className="w-4 h-4" />
                                Véhicule
                              </h4>
                              <div className="text-sm font-semibold text-foreground">
                                {offer.vehicle}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 pt-4 border-t border-border">
                          <Button variant="outline" size="default" className="w-full text-sm py-3">
                            <Eye className="w-4 h-4 mr-2" />
                            Voir les détails
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DashboardLayout>
    </>
  );
};

export default TransporterHistory;
